import { BaseApiService } from './BaseApiService';
import { User } from '../../types';

/**
 * User Service
 * Handles all user-related API operations
 */
export class UserService extends BaseApiService {
  private static readonly USER_STORAGE_KEY = 'gym_planner_user';

  /**
   * POST /api/users/register - Register a new user
   */
  async register(userData: { username: string; email: string; password: string }): Promise<{ message: string; user: User; token: string }> {
    return this.post<{ message: string; user: User; token: string }>('/users/register', userData);
  }

  /**
   * POST /api/users/login - Login user
   */
  async login(userData: { email: string; password: string }): Promise<{ message: string; user: User; token: string }> {
    return this.post<{ message: string; user: User; token: string }>('/users/login', userData);
  }

  /**
   * GET /api/users - Get all users
   */
  async getUsers(): Promise<User[]> {
    return this.get<User[]>('/users');
  }

  /**
   * GET /api/users/:id - Get user by ID
   */
  async getUserById(id: string): Promise<User> {
    return this.get<User>(`/users/${id}`);
  }

  /**
   * GET /api/users/me - Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return this.get<User>('/users/me');
  }

  /**
   * PUT /api/users/me/locale - Persist authenticated user's preferred locale
   */
  async updatePreferredLocale(locale: string): Promise<{ message: string; user: User; preferredLocale: string }> {
    return this.put<{ message: string; user: User; preferredLocale: string }>('/users/me/locale', { locale });
  }

  /**
   * POST /api/users/forgot-password (TODO.md J1) - Request a password reset link.
   *
   * Always resolves with the same generic `{ message }` on any 200 response, regardless of
   * whether the email matched an account - that anti-enumeration contract is enforced by the
   * backend, so callers must not try to infer account existence from this response.
   */
  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/users/forgot-password', { email });
  }

  /**
   * POST /api/users/reset-password (TODO.md J1) - Consume a reset token and set a new password.
   *
   * Rejects (via BaseApiService's error handling) with the server's message on a 400 - "Invalid
   * or expired reset link." for a bad/expired/already-used token, or the min-length message for a
   * too-short password.
   */
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.post<{ message: string }>('/users/reset-password', { token, newPassword });
  }

  /**
   * Get current user ID from localStorage
   * Falls back to demo user if not found
   */
  getCurrentUserId(): string {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) {
        return 'user_demo_1';
      }

      const userStr = localStorage.getItem(UserService.USER_STORAGE_KEY);
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || 'user_demo_1';
      }
    } catch (error) {
      console.error('Error getting current user ID:', error);
    }
    
    return 'user_demo_1';
  }

  /**
   * Save user to localStorage
   */
  saveUserToStorage(user: User): void {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) {
        return;
      }
      localStorage.setItem(UserService.USER_STORAGE_KEY, JSON.stringify(user));
    } catch (error) {
      console.error('Error saving user to storage:', error);
    }
  }

  /**
   * Get user from localStorage
   */
  getUserFromStorage(): User | null {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) {
        return null;
      }

      const userStr = localStorage.getItem(UserService.USER_STORAGE_KEY);
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting user from storage:', error);
      return null;
    }
  }

  /**
   * Clear user from localStorage
   */
  clearUserFromStorage(): void {
    try {
      if (typeof window === 'undefined' || !('localStorage' in window)) {
        return;
      }
      localStorage.removeItem(UserService.USER_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing user from storage:', error);
    }
  }
}
